import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { Pin, Star, GripVertical, Target, AlertCircle, ExternalLink, Layers } from "lucide-react";

const COLUMNS = [
    { key: "exploring", label: "Exploring", tone: "border-zinc-700", header: "text-zinc-300" },
    { key: "contacted", label: "Contacted", tone: "border-blue-500/40", header: "text-blue-300" },
    { key: "meeting", label: "Meeting", tone: "border-amber-500/40", header: "text-amber-300" },
    { key: "closed", label: "Closed", tone: "border-emerald-500/40", header: "text-emerald-300" },
];

// Per-stage staleness threshold (days)
const STALE_DAYS = { exploring: 14, contacted: 10, meeting: 7, closed: Infinity };

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${Number(n).toFixed(0)}`;
};

function tierColor(score) {
    if (score >= 90) return "#10B981";
    if (score >= 80) return "#3B82F6";
    if (score >= 70) return "#FACC15";
    return "#A1A1AA";
}

function daysSince(iso) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (isNaN(t)) return null;
    return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
}

const syKey = (s) => `${s.firm_a_id}:${s.firm_b_id}:${s.product_id}`;

const THEME_PALETTE = ["#F59E0B", "#3B82F6", "#10B981", "#EC4899", "#A855F7", "#06B6D4", "#F97316", "#84CC16", "#EF4444", "#8B5CF6"];
function themeColor(t) {
    if (!t) return "#71717A";
    let h = 0;
    for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) >>> 0;
    return THEME_PALETTE[h % THEME_PALETTE.length];
}

const UNTAGGED = "_untagged_";

export default function Board() {
    const [synergies, setSynergies] = useState(null);
    const [firms, setFirms] = useState([]);
    const [dragKey, setDragKey] = useState(null);
    const [overCol, setOverCol] = useState(null);
    const [groupByTheme, setGroupByTheme] = useState(false);

    const load = () => api.get("/synergies").then(({ data }) => setSynergies(data || []));
    useEffect(() => {
        load();
        api.get("/firms").then(({ data }) => setFirms(data || [])).catch(() => {});
    }, []);

    // Map firm id → {color, themes}
    const firmMeta = useMemo(() => {
        const m = {};
        firms.forEach((f) => { m[f.id] = { color: f.color || "", themes: f.themes || [] }; });
        return m;
    }, [firms]);

    const grouped = useMemo(() => {
        const out = { exploring: [], contacted: [], meeting: [], closed: [] };
        for (const s of (synergies || [])) {
            const k = COLUMNS.find((c) => c.key === s.status) ? s.status : "exploring";
            out[k].push(s);
        }
        return out;
    }, [synergies]);

    const move = async (s, newStatus) => {
        if ((s.status || "exploring") === newStatus) return;
        // optimistic update
        setSynergies((prev) => prev.map((x) => (syKey(x) === syKey(s) ? { ...x, status: newStatus } : x)));
        try {
            await api.patch(`/synergies/${syKey(s)}/status`, { status: newStatus });
        } catch {
            load(); // revert from server
        }
    };

    const stats_counts = useMemo(() => {
        const total = (synergies || []).length;
        let stale = 0;
        for (const s of (synergies || [])) {
            const status = s.status || "exploring";
            const age = daysSince(s.stage_changed_at);
            const threshold = STALE_DAYS[status];
            if (age !== null && threshold !== Infinity && age >= threshold) stale++;
        }
        return { total, stale };
    }, [synergies]);

    // Group synergies by primary theme of FIRM_A for swimlanes view.
    const themeLanes = useMemo(() => {
        if (!synergies) return [];
        const byTheme = new Map();
        for (const s of synergies) {
            const themes = firmMeta[s.firm_a_id]?.themes || [];
            const t = themes[0] || UNTAGGED;
            if (!byTheme.has(t)) byTheme.set(t, []);
            byTheme.get(t).push(s);
        }
        // sort lanes: by total projected revenue desc, then untagged last
        return [...byTheme.entries()]
            .map(([theme, items]) => ({
                theme,
                items,
                total: items.reduce((a, x) => a + (Number(x.projected_revenue) || 0), 0),
            }))
            .sort((a, b) => {
                if (a.theme === UNTAGGED) return 1;
                if (b.theme === UNTAGGED) return -1;
                return b.total - a.total;
            });
    }, [synergies, firmMeta]);

    /** Render one synergy card. Used by both classic columns and theme swimlanes. */
    const renderCard = (s, currentCol) => {
        const color = tierColor(s.fit_score || 0);
        const status = s.status || "exploring";
        const age = daysSince(s.stage_changed_at);
        const threshold = STALE_DAYS[status];
        const isStale = age !== null && threshold !== Infinity && age >= threshold;
        const meta = firmMeta[s.firm_a_id] || {};
        const firmColor = meta.color;
        return (
            <div
                key={syKey(s)}
                draggable
                onDragStart={() => setDragKey(syKey(s))}
                onDragEnd={() => setDragKey(null)}
                data-testid={`card-${syKey(s)}`}
                style={firmColor ? { borderLeftColor: firmColor, borderLeftWidth: "3px" } : undefined}
                className={`border bg-black p-3 cursor-grab active:cursor-grabbing hover:border-amber-500/40 transition-colors ${isStale ? "border-red-500/50" : "border-zinc-800"}`}
            >
                <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                        {s.pinned && <Pin className="w-3 h-3 text-amber-500 shrink-0" />}
                        {firmColor && <span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ background: firmColor }} />}
                        <div className="text-sm text-zinc-100 truncate">{s.firm_a_name} → {s.firm_b_name}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <Link
                            to={`/app/synergy?key=${encodeURIComponent(syKey(s))}`}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onDragStart={(e) => e.preventDefault()}
                            draggable={false}
                            title="Reopen synergy analysis"
                            data-testid={`reopen-${syKey(s)}`}
                            className="text-zinc-500 hover:text-amber-500 transition-colors"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                        <GripVertical className="w-3.5 h-3.5 text-zinc-700" />
                    </div>
                </div>
                <div className="font-mono text-[10px] text-amber-500 mb-2 truncate">{s.product_name}</div>
                <div className="flex items-center justify-between text-xs">
                    <div className="font-mono text-amber-500">{fmtMoney(s.projected_revenue)}</div>
                    <span className="inline-flex items-center gap-1 font-mono" style={{ color }}>
                        <Star className="w-3 h-3" style={{ fill: color }} /> {s.fit_score}
                    </span>
                </div>
                {age !== null && (
                    <div className="mt-2 flex items-center justify-between font-mono text-[10px]" data-testid={`age-${syKey(s)}`}>
                        <span className={isStale ? "text-red-400 inline-flex items-center gap-1" : "text-zinc-600"}>
                            {isStale && <AlertCircle className="w-3 h-3" />}
                            {age === 0 ? "today" : `${age}d in stage`}
                        </span>
                        {isStale && <span className="text-red-400 uppercase tracking-wider" data-testid={`stale-${syKey(s)}`}>stale</span>}
                    </div>
                )}
                <div className="flex gap-1 mt-2 flex-wrap md:hidden">
                    {COLUMNS.filter((c) => c.key !== currentCol).map((c) => (
                        <button
                            key={c.key}
                            onClick={() => move(s, c.key)}
                            data-testid={`move-${syKey(s)}-to-${c.key}`}
                            className="text-[9px] font-mono uppercase tracking-wider border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-500 px-1.5 py-0.5"
                        >
                            → {c.label}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    if (synergies === null) return <div className="p-12"><Loader label="Loading board" /></div>;

    if (synergies.length === 0) {
        return (
            <div className="p-8" data-testid="board-page">
                <header className="mb-8 border-b border-zinc-800 pb-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Action board</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Pipeline</h1>
                </header>
                <div className="border border-dashed border-zinc-800 p-16 text-center">
                    <Target className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-heading font-bold text-zinc-300 text-lg mb-2">No saved synergies yet</div>
                    <div className="text-sm text-zinc-500 mb-5">Run or pin a synergy first, then move it across stages here.</div>
                    <Link to="/app/synergy" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5">
                        Open Synergy
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8" data-testid="board-page">
            <header className="mb-8 border-b border-zinc-800 pb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Action board</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Pipeline</h1>
                    <p className="text-sm text-zinc-500 mt-2">Drag synergies across stages or click the chips to move them.</p>
                </div>
                <div className="font-mono text-xs text-zinc-500 flex items-center gap-4">
                    <span>{stats_counts.total} synerg{stats_counts.total === 1 ? "y" : "ies"} on board</span>
                    {stats_counts.stale > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-red-400" data-testid="stale-summary">
                            <AlertCircle className="w-3.5 h-3.5" /> {stats_counts.stale} stale
                        </span>
                    )}
                    <button
                        onClick={() => setGroupByTheme((x) => !x)}
                        data-testid="toggle-swimlanes-btn"
                        className={`inline-flex items-center gap-1.5 border text-[10px] font-mono uppercase tracking-wider px-2.5 py-1.5 transition-colors ${
                            groupByTheme ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-zinc-800 hover:border-zinc-600 text-zinc-400"
                        }`}
                    >
                        <Layers className="w-3 h-3" /> {groupByTheme ? "Swimlanes: on" : "Group by theme"}
                    </button>
                </div>
            </header>

            {groupByTheme ? (
                /* Theme swimlanes view: one row per theme, columns per stage */
                <div className="space-y-3" data-testid="board-swimlanes">
                    {themeLanes.length === 0 && (
                        <div className="border border-dashed border-zinc-800 p-12 text-center font-mono text-xs text-zinc-600">
                            No synergies to group yet.
                        </div>
                    )}
                    {themeLanes.map(({ theme, items, total }) => {
                        const tc = theme === UNTAGGED ? "#71717A" : themeColor(theme);
                        const laneGrouped = { exploring: [], contacted: [], meeting: [], closed: [] };
                        for (const s of items) {
                            const k = COLUMNS.find((c) => c.key === s.status) ? s.status : "exploring";
                            laneGrouped[k].push(s);
                        }
                        return (
                            <div key={theme} className="border border-zinc-800 bg-zinc-950" data-testid={`swimlane-${theme}`}>
                                <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between gap-2 flex-wrap" style={{ borderLeftWidth: "4px", borderLeftColor: tc }}>
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: tc }} />
                                        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-200">
                                            {theme === UNTAGGED ? "Untagged" : theme}
                                        </div>
                                        <span className="font-mono text-[10px] text-zinc-500">· {items.length} synerg{items.length === 1 ? "y" : "ies"} · {fmtMoney(total)}</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-px bg-zinc-800">
                                    {COLUMNS.map((col) => (
                                        <div
                                            key={col.key}
                                            data-testid={`swimlane-${theme}-${col.key}`}
                                            onDragOver={(e) => { e.preventDefault(); setOverCol(`${theme}|${col.key}`); }}
                                            onDragLeave={() => setOverCol((c) => (c === `${theme}|${col.key}` ? null : c))}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setOverCol(null);
                                                if (!dragKey) return;
                                                const target = synergies.find((s) => syKey(s) === dragKey);
                                                if (target) move(target, col.key);
                                                setDragKey(null);
                                            }}
                                            className={`bg-zinc-950 min-h-[80px] p-2 ${overCol === `${theme}|${col.key}` ? "bg-zinc-900/60" : ""}`}
                                        >
                                            <div className={`font-mono text-[9px] uppercase tracking-[0.2em] mb-2 ${col.header}`}>{col.label} · {laneGrouped[col.key].length}</div>
                                            <div className="space-y-2">
                                                {laneGrouped[col.key].map((s) => renderCard(s, col.key))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* Classic 4-column view */
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                    {COLUMNS.map((col) => (
                        <div
                            key={col.key}
                            data-testid={`column-${col.key}`}
                            onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
                            onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
                            onDrop={(e) => {
                                e.preventDefault();
                                setOverCol(null);
                                if (!dragKey) return;
                                const target = synergies.find((s) => syKey(s) === dragKey);
                                if (target) move(target, col.key);
                                setDragKey(null);
                            }}
                            className={`border bg-zinc-950 min-h-[200px] ${col.tone} ${overCol === col.key ? "bg-zinc-900/60" : ""}`}
                        >
                            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                                <div className={`font-mono text-[11px] uppercase tracking-[0.25em] ${col.header}`}>{col.label}</div>
                                <div className="font-mono text-[10px] text-zinc-500">{grouped[col.key].length}</div>
                            </div>
                            <div className="p-2 space-y-2">
                                {grouped[col.key].map((s) => renderCard(s, col.key))}
                                {grouped[col.key].length === 0 && (
                                    <div className="text-center text-[10px] font-mono uppercase tracking-wider text-zinc-700 py-6">Empty</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
